/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect } from 'react'

import { useSignal } from '@preact-signals/safe-react'

import { _getCacheKey } from './cache'
import { _doFetch } from './do-fetch'
import { _registerRefresh, _unregisterRefresh } from './refresh'
import { _FetchConfigData, _RequestDefinition } from './types'

const createUseFetch =
  (fetchConfigData: _FetchConfigData) =>
  <Def extends _RequestDefinition<any, any, any>>(
    def: Def,
    options?: {
      urlParams?: Def['urlParams']
      fetchParams?: Def['fetchParams']
      enabled?: boolean
      deps?: any[]
    }
  ) => {
    const {
      urlParams,
      fetchParams,
      enabled: enable = true,
      deps = []
    } = {
      ...def,
      ...options
    }

    const data = useSignal<Def['response'] | undefined>(undefined)
    const error = useSignal<any>(undefined)
    const isLoading = useSignal(false)

    const key = _getCacheKey({
      url: def.url,
      method: def.method,
      fetchParams,
      urlParams
    })

    const fetchData = useCallback(async () => {
      isLoading.value = true
      error.value = undefined
      try {
        const result = await _doFetch(fetchConfigData)(def, {
          urlParams,
          fetchParams
        })
        data.value = result

        return result
      } catch (err) {
        error.value = err
      } finally {
        isLoading.value = false
      }
    }, [JSON.stringify(urlParams), JSON.stringify(fetchParams), ...deps])

    useEffect(() => {
      const _fetch = () => {
        if (enable) {
          fetchData()
        }
      }
      _fetch()
      if (enable) {
        _registerRefresh(fetchConfigData)(key, _fetch)
        return () => _unregisterRefresh(fetchConfigData)(key, _fetch)
      }
    }, [fetchData, enable])

    return {
      data: data.value as Def["response"],
      error: error.value,
      isLoading: isLoading.value,
      refetch: fetchData
    }
  }

export { createUseFetch as _createUseFetch }
